/**
 * TwoFactorGate: a project that requires two-factor sign-in.
 *
 * The server refuses the project's requests with 403 + code MFA_REQUIRED
 * (utils/mfaGate) and apiFetch turns that into a `pidly:mfa-required`
 * event carrying the reason:
 *
 *   - 'enrol'    the person has not set two-factor up: the card sends
 *                them to the Account page, or out of the project.
 *   - 'recheck'  the project's window since the last code has passed
 *                (Project Settings, "Ask for the code again after"): the
 *                card takes the six-digit code (or a recovery code),
 *                POST /api/auth/2fa/recheck mints a token with a fresh
 *                mfaAt, the stored token is swapped and the page reloads.
 *
 * Drawn as the app's ONE dialog (utils/dialogs, the `pdlg-` classes).
 * Lives at the app root, outside Router, so any page gets the same card.
 */

import { useEffect, useState } from 'react';
import { apiFetch } from '../utils/apiFetch';
import { ensureDialogStyles } from '../utils/dialogs';
import { getStoredUser, setAuth } from '../utils/authStorage';

export default function TwoFactorGate() {
  const [block, setBlock] = useState(null); // { reason, orgName, afterLabel }
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    ensureDialogStyles();
    const onRequired = (e) => {
      const d = e?.detail || {};
      if (!d.reason) return;
      setBlock(b => b || { reason: d.reason, orgName: d.orgName || '', afterLabel: d.afterLabel || '' });
    };
    window.addEventListener('pidly:mfa-required', onRequired);
    return () => window.removeEventListener('pidly:mfa-required', onRequired);
  }, []);

  if (!block) return null;

  const name = block.orgName || 'This project';
  const leave = () => { window.location.href = '/overview'; };
  const setUp = () => { window.location.href = '/account'; };

  const submit = async (e) => {
    e.preventDefault();
    const raw = code.trim();
    if (!raw || busy) return;
    setBusy(true); setError('');
    try {
      const r = await apiFetch('/api/auth/2fa/recheck', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ code: raw }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.token) throw new Error(d.error || 'That code is not right');
      let remember = true;
      try { remember = !!localStorage.getItem('authToken'); } catch { /* keep */ }
      setAuth({ token: d.token, user: d.user || getStoredUser(), remember });
      window.location.reload();
    } catch (err) {
      setError(err.message || 'That code is not right');
      setBusy(false);
    }
  };

  if (block.reason === 'enrol') {
    return (
      <div className="pdlg-overlay">
        <div className="pdlg pidly-dialog-box" role="dialog" aria-modal="true" aria-labelledby="pidly-mfa-title">
          <h2 id="pidly-mfa-title" className="pdlg-title">Two-factor sign-in needed</h2>
          <div className="pdlg-body">{name} requires two-factor sign-in. Set it up on your Account page, then open the project again.</div>
          <div className="pdlg-foot">
            <button type="button" className="pdlg-btn pdlg-btn--quiet" onClick={leave}>Leave project</button>
            <button type="button" className="pdlg-btn pdlg-btn--primary" onClick={setUp}>Set up</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pdlg-overlay">
      <form className="pdlg pidly-dialog-box" role="dialog" aria-modal="true" aria-labelledby="pidly-mfa-title" onSubmit={submit}>
        <h2 id="pidly-mfa-title" className="pdlg-title">Enter your code</h2>
        <div className="pdlg-body">
          {name} asks for your authenticator code again{block.afterLabel && block.afterLabel !== 'each sign-in' ? ` after ${block.afterLabel}` : ''}. Type the six-digit code from your app, or a recovery code.
        </div>
        <input
          className="pdlg-input"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          placeholder="123456"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          aria-label="Code"
        />
        {error && <div className="pdlg-body" style={{ color: 'var(--danger, #d64545)', marginTop: 8 }}>{error}</div>}
        <div className="pdlg-foot">
          <button type="button" className="pdlg-btn pdlg-btn--quiet" onClick={leave} disabled={busy}>Leave project</button>
          <button type="submit" className="pdlg-btn pdlg-btn--primary" disabled={busy || !code.trim()}>{busy ? 'Checking…' : 'Continue'}</button>
        </div>
      </form>
    </div>
  );
}
